import { useState } from "react";
import useSignIn from "react-auth-kit/hooks/useSignIn";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { logUser } from "../constants/urls";

export default function LogIn() {
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA state machine: "LOGIN", "MFA_REQUIRED", "MFA_SETUP", "EMAIL_VERIFICATION_REQUIRED", "PASSWORD_RESET_REQUIRED"
  const [step, setStep] = useState("LOGIN"); 
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [mfaQrCode, setMfaQrCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const signIn = useSignIn();
  const navigate = useNavigate();

  const signInwithAuth = (user) => {
    const isSignedIn = signIn({
      auth: {
        token: user?.token,
        type: "Bearer",
      },
      refresh: "",
      userState: {
        name: user.name,
        email: user.email,
        type: user.userType,
        department: user.department,
      },
    });

    if (isSignedIn) {
      navigate("/");
    } else {
      setError("Failed to create session. Please try again.");
    }
  };

  const signInFunctionHandler = async (e) => {
    e?.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Please enter both email and password.");
      return;
    }

    if (step === "PASSWORD_RESET_REQUIRED") {
      if (newPassword !== confirmNewPassword) {
        setError("New passwords do not match.");
        return;
      }
      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters long.");
        return;
      }
    }

    try {
      setLoading(true);
      const payload = { email: username, password: password };
      if (step === "MFA_REQUIRED" || step === "MFA_SETUP" || step === "EMAIL_VERIFICATION_REQUIRED" || step === "PASSWORD_RESET_REQUIRED") {
        payload.mfaCode = mfaCode;
      }
      if (step === "EMAIL_VERIFICATION_REQUIRED") {
        payload.emailCode = emailCode;
      }
      if (step === "PASSWORD_RESET_REQUIRED") {
        payload.newPassword = newPassword;
      }

      let url = logUser;
      if (step === "MFA_SETUP") {
        url = logUser.replace("/login", "/verify-mfa-setup");
      } else if (step === "EMAIL_VERIFICATION_REQUIRED") {
        url = logUser.replace("/login", "/verify-email-code");
      } else if (step === "PASSWORD_RESET_REQUIRED") {
        url = logUser.replace("/login", "/reset-temp-password");
      }

      const resp = await axios.post(
        url,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (resp.data?.status === 401) {
        if (resp.data?.message === "No User Found") {
          setError("No user found with this email.");
        } else {
          setError(resp.data?.message || "Username or password is incorrect.");
        }
      } else if (resp.data?.status === 402) {
        setStep("MFA_REQUIRED");
      } else if (resp.data?.status === 403) {
        setStep("MFA_SETUP");
        setMfaSecret(resp.data?.message?.secret);
        setMfaQrCode(resp.data?.message?.qrCode);
      } else if (resp.data?.status === 405) {
        setStep("EMAIL_VERIFICATION_REQUIRED");
      } else if (resp.data?.status === 406) {
        setStep("PASSWORD_RESET_REQUIRED");
      } else if (resp.data?.status === 200) {
        const message = resp.data?.message;
        signInwithAuth(message);
      } else {
        setError(resp.data?.message || "Unexpected response from server. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      {/* Background blur circles */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-amber-400/20 blur-3xl" />
      </div>

      <div className="relative w-full max-w-6xl mx-auto my-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left side - branding / info */}
          <div className="lg:col-span-5 flex flex-col text-slate-100 space-y-6 lg:pt-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium backdrop-blur self-start">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Secure Admin Portal
            </div>

            <h1 className="text-3xl lg:text-4xl font-semibold leading-tight">
              Welcome back to{" "}
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                FIO Admin
              </span>
            </h1>

            <p className="text-sm text-slate-300/80 leading-relaxed">
              Sign in to manage users, roles, and permissions across the platform.
              Keep your workspace secure and in sync with your organisation.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 text-xs text-slate-200">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                <p className="text-amber-300 text-sm font-semibold">Centralised control</p>
                <p className="mt-1 text-slate-300/80">
                  Manage all admin and blog users from a single dashboard.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                <p className="text-indigo-300 text-sm font-semibold">Reliable access</p>
                <p className="mt-1 text-slate-300/80">
                 Gain secure entry with encrypted sessions and role-based controls.
                </p>
              </div>
            </div>
          </div>

          {/* Right side - login card */}
          <div className="lg:col-span-7 relative w-full">
            <div className="rounded-2xl border border-white/10 bg-slate-900/80 shadow-2xl shadow-black/40 backdrop-blur-xl px-6 py-8 sm:px-8 sm:py-10">
              <div className="flex flex-col items-center mb-6">
                <img
                  className="h-16 w-auto mb-3"
                  src="/images/fems-fio-logo-03.png"
                  alt="Hemas"
                />
                <h2 className="text-xl font-semibold text-slate-50">
                  Sign in to your account
                </h2>
                <p className="mt-1 text-xs text-slate-400">
                  Use your admin credentials to continue
                </p>
              </div>

              {error && (
                <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                  {error}
                </div>
              )}

              {step === "LOGIN" && (
                <form className="space-y-5" onSubmit={signInFunctionHandler}>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-medium text-slate-200"
                    >
                      Email address
                    </label>
                    <div className="mt-1.5">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={username}
                        onChange={(e) => setUserName(e.target.value)}
                        required
                        className="block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-2 text-sm text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-500 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="password"
                        className="block text-xs font-medium text-slate-200"
                      >
                        Password
                      </label>
                    </div>
                    <div className="mt-1.5">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-2 text-sm text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-500 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Only authorised users can access this portal.</span>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/30 transition hover:from-amber-400 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Signing you in…" : "Sign in"}
                    </button>
                  </div>
                </form>
              )}

              {step === "MFA_REQUIRED" && (
                <form className="space-y-5" onSubmit={signInFunctionHandler}>
                  <div>
                    <label
                      htmlFor="mfaCode"
                      className="block text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1"
                    >
                      Two-Factor Authentication
                    </label>
                    <p className="text-[11px] text-slate-400 mb-3">
                      Enter the 6-digit verification code from your Authenticator app to continue.
                    </p>
                    <div className="mt-1.5">
                      <input
                        id="mfaCode"
                        name="mfaCode"
                        type="text"
                        pattern="[0-9]*"
                        inputMode="numeric"
                        maxLength={6}
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        required
                        className="block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-3 text-center text-lg font-bold tracking-widest text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                        placeholder="000000"
                      />
                    </div>
                  </div>

                  <div className="pt-2 space-y-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/30 transition hover:from-amber-400 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Verifying…" : "Verify Code"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStep("LOGIN");
                        setMfaCode("");
                      }}
                      className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition"
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              )}

              {step === "MFA_SETUP" && (
                <form className="space-y-6" onSubmit={signInFunctionHandler}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    
                    {/* Setup side */}
                    <div className="flex flex-col items-center">
                      <label className="block text-xs font-semibold text-amber-300 uppercase tracking-wide mb-2 self-start">
                        Set Up Authenticator App
                      </label>
                      <p className="text-[11px] text-slate-400 mb-4 self-start leading-relaxed">
                        Scan this QR code with Google Authenticator, Microsoft Authenticator, or Duo.
                      </p>
                      
                      {/* QR Code Container */}
                      <div className="bg-white p-3 rounded-xl shadow-inner mb-4">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(mfaQrCode)}`} 
                          alt="MFA Setup QR Code"
                          className="h-40 w-40"
                        />
                      </div>

                      <div className="w-full bg-slate-950/40 border border-white/5 rounded-lg p-2.5 text-center mb-4">
                        <span className="text-[10px] text-slate-500 uppercase font-semibold">Or enter manually:</span>
                        <p className="text-xs font-mono font-bold text-amber-400 tracking-wide mt-1 select-all">{mfaSecret}</p>
                      </div>

                      <div className="w-full">
                        <label htmlFor="setupMfaCode" className="block text-xs font-medium text-slate-200 mb-1.5">
                          Enter 6-Digit Code
                        </label>
                        <input
                          id="setupMfaCode"
                          name="setupMfaCode"
                          type="text"
                          pattern="[0-9]*"
                          inputMode="numeric"
                          maxLength={6}
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value)}
                          required
                          className="block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-2 text-center text-lg font-bold tracking-widest text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                          placeholder="000000"
                        />
                      </div>
                    </div>

                    {/* Guides & Disclaimers side */}
                    <div className="space-y-4 bg-slate-950/30 border border-white/5 rounded-xl p-4 text-xs">
                      <div>
                        <h4 className="font-bold text-indigo-300 uppercase tracking-wider mb-2">Setup Guide</h4>
                        <ol className="list-decimal list-inside space-y-1.5 text-slate-300 leading-relaxed">
                          <li>Download an Authenticator app from App Store or Play Store.</li>
                          <li>Open the app, select <strong className="text-slate-100">Add Account</strong>, then scan the QR code.</li>
                          <li>Enter the 6-digit verification code to complete activation.</li>
                        </ol>
                      </div>

                      <hr className="border-white/5 my-3" />

                      <div>
                        <h4 className="font-bold text-rose-400 uppercase tracking-wider mb-2">⚠️ Security Disclaimers</h4>
                        <ul className="list-disc list-inside space-y-1.5 text-slate-400 leading-relaxed">
                          <li>Keep your setup secret private. Never screenshot or share this screen.</li>
                          <li>Write down the backup key shown below the QR code and store it in a secure location (e.g., password manager).</li>
                          <li>Ensure screen lock is configured on your mobile phone.</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 space-y-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/30 transition hover:from-amber-400 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Completing Setup…" : "Verify & Enable MFA"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStep("LOGIN");
                        setMfaCode("");
                      }}
                      className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition"
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              )}

              {step === "EMAIL_VERIFICATION_REQUIRED" && (
                <form className="space-y-5" onSubmit={signInFunctionHandler}>
                  <div>
                    <label
                      htmlFor="emailCode"
                      className="block text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1"
                    >
                      Verify Email Address
                    </label>
                    <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                      For corporate security, all admins must verify their email address every 6 months. A 6-digit confirmation code has been dispatched to your email.
                    </p>
                    <div className="mt-1.5">
                      <input
                        id="emailCode"
                        name="emailCode"
                        type="text"
                        pattern="[0-9]*"
                        inputMode="numeric"
                        maxLength={6}
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                        required
                        className="block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-3 text-center text-lg font-bold tracking-widest text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                        placeholder="000000"
                      />
                    </div>
                  </div>

                  <div className="pt-2 space-y-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/30 transition hover:from-amber-400 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Verifying Email Code…" : "Confirm Verification"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStep("LOGIN");
                        setEmailCode("");
                      }}
                      className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition"
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              )}

              {step === "PASSWORD_RESET_REQUIRED" && (
                <form className="space-y-5" onSubmit={signInFunctionHandler}>
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-xs font-semibold text-amber-300 uppercase tracking-wide mb-1"
                    >
                      Reset Temporary Password
                    </label>
                    <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                      Your password was set by an administrator. For security reasons, you must change your password on first login.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="newPassword" className="block text-xs font-medium text-slate-200">
                          New Password
                        </label>
                        <input
                          id="newPassword"
                          name="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          className="mt-1.5 block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-2 text-sm text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-500 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                          placeholder="••••••••"
                        />
                      </div>

                      <div>
                        <label htmlFor="confirmNewPassword" className="block text-xs font-medium text-slate-200">
                          Confirm New Password
                        </label>
                        <input
                          id="confirmNewPassword"
                          name="confirmNewPassword"
                          type="password"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          required
                          className="mt-1.5 block w-full rounded-lg border border-slate-600/60 bg-slate-900/60 px-3 py-2 text-sm text-slate-50 shadow-sm outline-none ring-0 placeholder:text-slate-500 focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 space-y-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-orange-950/30 transition hover:from-amber-400 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Resetting Password…" : "Reset Password & Login"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setStep("LOGIN");
                        setNewPassword("");
                        setConfirmNewPassword("");
                      }}
                      className="w-full text-center text-xs text-slate-400 hover:text-slate-200 transition"
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              )}

              <p className="mt-5 text-center text-[11px] text-slate-500">
                © {new Date().getFullYear()} Hemas FIO. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
